// formSteps.jsx — the StepIndicator + individual wizard step bodies.
// Step1 = client personal; Step2..Step5 operate on data.tripData (trip-level).
import React from "react";
import { Inp, Sel, Txa } from "../ui";
import { fmtMoney } from "../utils";
import { Ico, PayBar } from "./primitives";
import {
  FlightRow, HotelRow, VisaRow, TourRow, TransferRow, InsuranceRow, ServiceBlock,
} from "./serviceRows";
import { G, GL, GR, CURRENCIES } from "./constants";
import { CountryFields } from "./CountrySelect";
import TripTemplatePicker from "./TripTemplatePicker";
import ClientNameInput from "./ClientNameInput";
import RefundForm from "../refund/RefundForm";
import { BRAND } from "../brand";

// ─── STEP INDICATOR (generic over a steps array) ─────────────────────────────
// When `onStepClick` is supplied, each step becomes a button so the user can
// jump straight to any step instead of only going Back/Next.
export function StepIndicator({ steps, current, onStepClick }) {
  const clickable = typeof onStepClick === "function";
  return (
    <div className="flex items-center justify-between mb-7">
      {steps.map((s, i) => {
        const done = current > s.id, active = current === s.id;
        const Tag = clickable ? "button" : "div";
        return (
          <React.Fragment key={s.id}>
            <Tag
              type={clickable ? "button" : undefined}
              onClick={clickable ? () => onStepClick(s.id) : undefined}
              className={`flex flex-col items-center gap-1.5 ${clickable ? "cursor-pointer group" : ""}`}
              title={clickable ? `Go to ${s.label}` : undefined}
            >
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 ${done ? "bg-emerald-500" : active ? "" : "bg-slate-100"} ${clickable && !active ? "group-hover:ring-2 group-hover:ring-[rgb(var(--brand-rgb)/0.3)]" : ""}`}
                style={active ? { background: G, boxShadow: `0 4px 12px ${GR}` } : {}}
              >
                {done
                  ? <Ico d="M5 13l4 4L19 7" sz={13} col="white" />
                  : <Ico d={s.d} sz={13} col={active ? "white" : "#94a3b8"} />
                }
              </div>
              <span className={`text-[9px] font-black uppercase tracking-widest ${active ? "text-slate-700" : done ? "text-emerald-500" : "text-slate-300"}`}>
                {s.label}
              </span>
            </Tag>
            {i < steps.length - 1 && (
              <div className="flex-1 h-px mx-2 mb-5" style={{ background: current > s.id ? "#6ee7b7" : "#e2e8f0" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── STEP 1 — Personal (client-level) ────────────────────────────────────────
// suggestNames: show existing clients matching the typed name (new clients only).
export function Step1({ data, onChange, suggestNames = false, onOpenExisting }) {
  const set = (k) => (e) =>
    onChange({ ...data, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {suggestNames ? (
          <ClientNameInput value={data.fullName} onChange={set("fullName")} onOpenExisting={onOpenExisting} />
        ) : (
          <Inp label="Full Name *" placeholder="Jane Doe" value={data.fullName} onChange={set("fullName")} required />
        )}
        <Inp label="Email *" type="email" placeholder="jane@example.com" value={data.email} onChange={set("email")} required />
        <CountryFields data={data} onChange={onChange} />
      </div>
      <label className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 hover:border-slate-300 cursor-pointer transition-all">
        <input
          type="checkbox"
          checked={!!data.returningClient}
          onChange={set("returningClient")}
          className="w-4 h-4 accent-[rgb(var(--brand-rgb))]"
        />
        <div>
          <p className="text-sm font-semibold text-slate-700">Returning Client</p>
          <p className="text-xs text-slate-400">This client has booked with us before</p>
        </div>
        {data.returningClient && (
          <span className="ml-auto text-[10px] font-black px-2 py-0.5 rounded-full" style={{ background: GL, color: G }}>
            ↩ YES
          </span>
        )}
      </label>
    </div>
  );
}

// ─── STEP 2 — Trip (destinations stored as raw string, split at submit) ──────
export function Step2({ data, onChange }) {
  const t = data.tripData || {};

  const set = (k) => (e) =>
    onChange({ ...data, tripData: { ...t, [k]: e.target.value } });

  const setDest = (e) =>
    onChange({ ...data, tripData: { ...t, destinations: e.target.value } });

  const setNote = (k) => (e) =>
    onChange({ ...data, tripData: { ...t, notes: { ...(t.notes || {}), [k]: e.target.value } } });

  return (
    <div className="space-y-4">
      {/* Prefill from an existing trip package — optional, everything stays editable */}
      <TripTemplatePicker
        onApply={(fields) =>
          onChange({ ...data, tripData: { ...t, ...fields } })
        }
      />
      <Inp
        label="Destinations (comma separated)"
        placeholder="Dubai, Abu Dhabi"
        value={Array.isArray(t.destinations) ? t.destinations.join(", ") : t.destinations || ""}
        onChange={setDest}
      />
      <Inp
        label="Services Included"
        placeholder="Visa + Flight + Hotel"
        value={t.service || ""}
        onChange={set("service")}
      />
      <div className="grid grid-cols-2 gap-4">
        <Sel
          label="Trip Type"
          value={t.tripType || ""}
          onChange={set("tripType")}
          options={[
            { value: "visa", label: "Visa" },
            { value: "grouptrip", label: "Group Trip" },
            { value: "privatetrip", label: "Private Trip" },
            { value: "other", label: "Other" },
          ]}
        />
        <Sel
          label="Progress Status"
          value={t.progressStatus || ""}
          onChange={set("progressStatus")}
          options={[
            { value: "planning", label: "Planning" },
            { value: "in_progress", label: "In Progress" },
            { value: "confirmed", label: "Confirmed" },
            { value: "completed", label: "Completed" },
            { value: "cancelled", label: "Cancelled" },
            { value: "paused", label: "Paused" },
          ]}
        />
        <Inp label="Start Date" type="date" value={t.tripStartDate || ""} onChange={set("tripStartDate")} />
        <Inp label="End Date" type="date" value={t.tripEndDate || ""} onChange={set("tripEndDate")} min={t.tripStartDate || undefined} />
        <Inp label="Next Follow-up" type="date" value={t.notes?.nextFollowUpDate || ""} onChange={setNote("nextFollowUpDate")} />
      </div>
      <Txa
        label="Internal Notes"
        placeholder="e.g. Client prefers window seat…"
        value={t.notes?.internalNotes || ""}
        onChange={setNote("internalNotes")}
      />
    </div>
  );
}

// ─── STEP 3 — Payment ────────────────────────────────────────────────────────
export function Step3({ data, onChange }) {
  const p = data.tripData?.payment || {};
  const set = (k) => (e) =>
    onChange({ ...data, tripData: { ...(data.tripData || {}), payment: { ...p, [k]: e.target.value } } });

  // Multi-currency: payments made in OTHER currencies, each carrying the
  // equivalent value in this trip's base currency (`amountInBase`).
  const baseCur = p.currency || BRAND.currency;
  const addl = p.additionalPayments || [];
  const setAddl = (next) =>
    onChange({ ...data, tripData: { ...(data.tripData || {}), payment: { ...p, additionalPayments: next } } });
  const addRow = () =>
    setAddl([
      ...addl,
      {
        currency: "",
        foreignAmount: "",
        amountInBase: "",
        paymentMethod: "",
        note: "",
        date: new Date().toISOString().slice(0, 10), // defaults to today; editable
      },
    ]);
  const updRow = (i, k, v) => setAddl(addl.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const delRow = (i) => setAddl(addl.filter((_, idx) => idx !== i));
  const extraInBase = addl.reduce((s, r) => s + (Number(r.amountInBase) || 0), 0);
  const totalPaidBase = (Number(p.amountPaidToDate) || 0) + extraInBase;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Inp label="Total Trip Cost" type="number" placeholder="5000" value={p.totalTripCost || ""} onChange={set("totalTripCost")} />
        <Inp label="Deposit Required" type="number" placeholder="1000" value={p.depositRequired || ""} onChange={set("depositRequired")} />
        <Inp label="Amount Paid to Date" type="number" placeholder="500" value={p.amountPaidToDate || ""} onChange={set("amountPaidToDate")} />
        <Inp label="Next Payment Due" type="date" value={p.nextPaymentDueDate || ""} onChange={set("nextPaymentDueDate")} />
        <Inp label="Payment Date" type="date" value={p.date || ""} onChange={set("date")} />
        <Sel
          label="Payment Status"
          value={p.paymentStatus || ""}
          onChange={set("paymentStatus")}
          options={[
            { value: "unpaid", label: "Unpaid" },
            { value: "partial", label: "Partial" },
            { value: "paid", label: "Paid" },
            { value: "refunded", label: "Refunded" },
          ]}
        />
        <Sel
          label="Payment Method"
          value={p.paymentMethod || ""}
          onChange={set("paymentMethod")}
          options={[
            { value: "bank_transfer", label: "Bank Transfer" },
            { value: "cash", label: "Cash" },
            { value: "card", label: "Card" },
            { value: "crypto", label: "Crypto" },
            { value: "other", label: "Other" },
          ]}
        />
        <Sel
          label="Currency"
          value={p.currency || BRAND.currency}
          onChange={set("currency")}
          options={[
            { value: "USD", label: "USD – US Dollar" },
            { value: "EUR", label: "EUR – Euro" },
            { value: "GBP", label: "GBP – British Pound" },
            { value: "NGN", label: "NGN – Nigerian Naira" },
            { value: "GHS", label: "GHS – Ghanaian Cedi" },
            { value: "KES", label: "KES – Kenyan Shilling" },
            { value: "ZAR", label: "ZAR – South African Rand" },
            { value: "AED", label: "AED – UAE Dirham" },
            { value: "CAD", label: "CAD – Canadian Dollar" },
            { value: "AUD", label: "AUD – Australian Dollar" },
          ]}
        />
      </div>
      {Number(p.totalTripCost) > 0 && (
        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
            Payment Preview ({baseCur})
          </p>
          <PayBar paid={totalPaidBase} total={Number(p.totalTripCost) || 0} currency={baseCur} />
        </div>
      )}

      {/* ── Payments in other currencies ──────────────────────────────────── */}
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              Other-Currency Payments
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Enter the equivalent in {baseCur} — it’s added to the {baseCur} total above.
            </p>
          </div>
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-black whitespace-nowrap border border-dashed transition-colors"
            style={{ color: G, borderColor: GR, background: GL }}
          >
            <Ico d="M12 4v16m8-8H4" sz={11} col={G} /> Add Payment
          </button>
        </div>

        {!addl.length ? (
          <p className="px-4 py-4 text-xs text-slate-400">
            No other-currency payments recorded. All payments are in {baseCur}.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {addl.map((r, i) => (
              <div key={i} className="p-3 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Payment {i + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => delRow(i)}
                    className="text-slate-300 hover:text-rose-500 transition-colors"
                    title="Remove"
                  >
                    <Ico d="M6 18L18 6M6 6l12 12" sz={12} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Sel
                    label="Currency Paid"
                    value={r.currency || ""}
                    onChange={(e) => updRow(i, "currency", e.target.value)}
                    options={CURRENCIES}
                  />
                  <Inp
                    label="Amount Paid"
                    type="number"
                    placeholder="0.00"
                    value={r.foreignAmount || ""}
                    onChange={(e) => updRow(i, "foreignAmount", e.target.value)}
                  />
                  <Inp
                    label={`Equivalent in ${baseCur} *`}
                    type="number"
                    placeholder="0.00"
                    value={r.amountInBase || ""}
                    onChange={(e) => updRow(i, "amountInBase", e.target.value)}
                  />
                  <Sel
                    label="Method"
                    value={r.paymentMethod || ""}
                    onChange={(e) => updRow(i, "paymentMethod", e.target.value)}
                    options={[
                      { value: "bank_transfer", label: "Bank Transfer" },
                      { value: "cash", label: "Cash" },
                      { value: "card", label: "Card" },
                      { value: "crypto", label: "Crypto" },
                      { value: "other", label: "Other" },
                    ]}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {/* slice(0,10): Mongo returns full ISO datetimes; date inputs need YYYY-MM-DD */}
                  <Inp
                    label="Payment Date"
                    type="date"
                    value={(r.date || "").slice(0, 10)}
                    onChange={(e) => updRow(i, "date", e.target.value)}
                  />
                  <Inp
                    label="Note"
                    placeholder="e.g. paid in EUR via Wise"
                    value={r.note || ""}
                    onChange={(e) => updRow(i, "note", e.target.value)}
                  />
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Effective Total Paid ({baseCur})
              </span>
              <span className="text-sm font-black text-emerald-600">
                {fmtMoney(totalPaidBase, baseCur)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── STEP 4 — Services ───────────────────────────────────────────────────────
export function Step4({ data, onChange }) {
  const t = data.tripData || {};
  const mk = (k) => (v) => onChange({ ...data, tripData: { ...t, [k]: v } });
  return (
    <div className="space-y-4">
      <ServiceBlock title="Flights" items={t.flights || []} setItems={mk("flights")}
        emptyRow={{ airline: "", pnr: "", departureDate: "", returnDate: "", costAmt: "", depositAmt: "", paidAmt: "", status: "" }}
        RowComponent={FlightRow} />
      <ServiceBlock title="Hotels" items={t.hotels || []} setItems={mk("hotels")}
        emptyRow={{ name: "", confirmation: "", checkIn: "", checkOut: "", costAmt: "", depositAmt: "", paidAmt: "", status: "" }}
        RowComponent={HotelRow} />
      <ServiceBlock title="Visa" items={t.visa || []} setItems={mk("visa")}
        emptyRow={{ type: "", visaStatus: "", dateSubmitted: "", documentsRequired: "", documentsSubmitted: "", costAmt: "", depositAmt: "", paidAmt: "" }}
        RowComponent={VisaRow} />
      <ServiceBlock title="Tours" items={t.tours || []} setItems={mk("tours")}
        emptyRow={{ tourName: "", tourDate: "", costAmt: "", depositAmt: "", paidAmt: "", status: "" }}
        RowComponent={TourRow} />
      <ServiceBlock title="Airport Transfers" items={t.airportTransfers || []} setItems={mk("airportTransfers")}
        emptyRow={{ bookingDate: "", costAmt: "", depositAmt: "", paidAmt: "", status: "" }}
        RowComponent={TransferRow} />
      <ServiceBlock title="Insurance" items={t.insurance || []} setItems={mk("insurance")}
        emptyRow={{ provider: "", policyNumber: "", startDate: "", endDate: "", costAmt: "", depositAmt: "", paidAmt: "", status: "" }}
        RowComponent={InsuranceRow} />
    </div>
  );
}

// ─── STEP 5 — Refund ─────────────────────────────────────────────────────────
// Step 5 — Refund. Uses the SAME RefundForm as the client's Refund tab, so a refund
// entered here becomes a real refund record (visible in the Refund tab and the Refunds
// section). The client may not exist yet, so the refund is kept as a draft in
// data.refundDraft and created by the parent right after the client/trip is saved.
export function Step5({ data, onChange }) {
  const draft = data.refundDraft || null;
  const [editing, setEditing] = React.useState(false);
  const t = data.tripData || {};
  const dest = Array.isArray(t.destinations) ? t.destinations.join(", ") : t.destinations || "";

  // Prefill from what's already in the wizard; the final client details are applied on save.
  const prefill = draft || {
    clientName: data.fullName || "",
    email: data.email || "",
    phone: data.phone || "",
    country: data.countryOfResidence || "",
    destination: dest,
    currency: t.payment?.currency || BRAND.currency,
    originalAmount: t.payment?.amountPaidToDate || "",
  };

  if (editing) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-slate-500 leading-relaxed p-3 rounded-xl bg-slate-50 border border-slate-100">
          Fill in the refund, then click <b>{draft ? "Update Refund" : "Create Refund"}</b> to attach it.
          It's saved when you finish this form — linked to the client and shown in the Refunds section.
        </p>
        <RefundForm
          initial={draft ? { ...draft, _id: "draft" } : prefill}
          clients={[]}
          receipts={[]}
          onSubmit={(form) => { onChange({ ...data, refundDraft: form }); setEditing(false); }}
          onClose={() => setEditing(false)}
        />
      </div>
    );
  }

  if (draft) {
    const isCredit = draft.refundType === "kai_credit";
    return (
      <div className="space-y-4">
        <p className="text-xs text-slate-400 leading-relaxed">
          This refund will be created together with the client.
        </p>
        <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-200">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-[10px] font-black flex-shrink-0"
            style={{ background: isCredit ? G : "#f59e0b" }}
          >
            {isCredit ? "CR" : "REF"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-800">
              {fmtMoney(draft.refundAmount, draft.currency || "USD")}
              <span className="text-slate-400 font-semibold"> of {fmtMoney(draft.originalAmount, draft.currency || "USD")}</span>
            </p>
            <p className="text-[11px] text-slate-400 truncate">
              {(draft.refundStatus || "not_started").replace(/_/g, " ")}
              {draft.reasonForRefund && ` · ${draft.reasonForRefund}`}
            </p>
          </div>
          <button type="button" onClick={() => setEditing(true)}
            className="text-xs font-bold px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors">
            Edit
          </button>
          <button type="button" onClick={() => onChange({ ...data, refundDraft: null })}
            className="text-xs font-bold px-2.5 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 transition-colors">
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
      <span className="text-3xl mb-2">💸</span>
      <p className="text-sm font-bold text-slate-500 mb-1">No refund</p>
      <p className="text-xs text-slate-400 mb-4 max-w-xs">
        Optional — record a refund if the client has requested one. It'll appear in the Refunds section too.
      </p>
      <button type="button" onClick={() => setEditing(true)}
        className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl text-white"
        style={{ background: G }}>
        <Ico d="M12 4v16m8-8H4" sz={11} col="white" /> Record Refund
      </button>
    </div>
  );
}
